import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EventsGrid, { EventItem } from '@/panel/components/EventsGrid';
import { supabase } from '@/integrations/supabase/client';
import topGradient from '@/assets/top-gradient.png';
import { CLIENT_ID_BLOOMKEEP } from '@/lib/constants';
import { parseEventDateValue } from '@/panel/utils/dateTime';
const FALLBACK_KEYS = ['image_cover', 'text_home_h1'] as const;

function fallbackTitleFromSlug(slug: string): string {
  return slug
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

function getEventDateTimestamp(value?: string | null, fallback = Number.POSITIVE_INFINITY) {
  const parsed = parseEventDateValue(value);
  return parsed ? parsed.getTime() : fallback;
}

function isPastEventDate(value: string | undefined, now: Date) {
  const parsed = parseEventDateValue(value);
  if (!parsed) return false;

  const dayOnly = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return dayOnly < todayOnly;
}

const EventsList: React.FC = () => {
  const [items, setItems] = useState<EventItem[]>([]);
  const [internalItems, setInternalItems] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data: albums, error } = await supabase
          .from('albums')
          .select('id, slug, is_active, created_at, updated_at, event_date, is_reserved, client_id')
          .or(`client_id.is.null,client_id.neq.${CLIENT_ID_BLOOMKEEP}`); // ignores BK client in main lists
        if (error) throw error;

        const { data: internalAlbums, error: internalErr } = await supabase
          .from('albums')
          .select('id, slug, is_active, created_at, updated_at, event_date, is_reserved, client_id')
          .eq('client_id', CLIENT_ID_BLOOMKEEP);
        if (internalErr) throw internalErr;

        const ids = (albums || []).map((a: any) => a.id);
        const internalIds = (internalAlbums || []).map((a: any) => a.id);
        const coverMap = new Map<string, string>();
        const titleMap = new Map<string, string>();
        if (ids.length > 0 || internalIds.length > 0) {
          const { data: settingsRows, error: settingsErr } = await supabase
            .from('settings')
            .select('album_id, key, value')
            .in('album_id', [...ids, ...internalIds])
            .in('key', Array.from(FALLBACK_KEYS));
          if (!settingsErr && Array.isArray(settingsRows)) {
            settingsRows.forEach((row: any) => {
              if (!row?.album_id) return;
              if (row.key === 'image_cover' && row.value) {
                coverMap.set(row.album_id, String(row.value));
              } else if (row.key === 'text_home_h1' && typeof row.value === 'string') {
                titleMap.set(row.album_id, row.value.trim());
              }
            });
          }
        }

        const mapped: EventItem[] = (albums || []).map((row: any) => ({
          id: row.id,
          image: coverMap.get(row.id) || topGradient,
          title: (titleMap.get(row.id) || '').trim() || fallbackTitleFromSlug(row.slug),
          slug: row.slug,
          event_date: row.event_date || '',
          is_active: !!row.is_active,
          is_reserved: !!row.is_reserved,
          admins: [],
          created_at: row.created_at,
          updated_at: row.updated_at,
        }));
        // Order by event_date ascending (nulls last)
        mapped.sort((a, b) => {
          const ta = getEventDateTimestamp(a.event_date);
          const tb = getEventDateTimestamp(b.event_date);
          return ta - tb;
        });
        const internalMapped: EventItem[] = (internalAlbums || []).map((row: any) => ({
          id: row.id,
          image: coverMap.get(row.id) || topGradient,
          title: (titleMap.get(row.id) || '').trim() || fallbackTitleFromSlug(row.slug),
          slug: row.slug,
          event_date: row.event_date || '',
          is_active: !!row.is_active,
          is_reserved: !!row.is_reserved,
          admins: [],
          created_at: row.created_at,
          updated_at: row.updated_at,
        }));

        // sort
        internalMapped.sort((a, b) => {
          const ta = getEventDateTimestamp(a.event_date);
          const tb = getEventDateTimestamp(b.event_date);
          return ta - tb;
        });

        setItems(mapped);
        setInternalItems(internalMapped);
      } catch (e) {
        console.error('Failed to fetch events', e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const headerText = useMemo(() => {
    if (loading) return 'Cargando eventos…';
    return `${items.length} evento${items.length === 1 ? '' : 's'}`;
  }, [items.length, loading]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();

    const norm = (s?: string) => (s || '').toLowerCase();
    const q = search.trim().toLowerCase();

    const matches = (a: EventItem) => {
      if (!q) return true;
      return norm(a.title).includes(q) || norm(a.slug).includes(q);
    };

    const filtered = items.filter(matches);
    const upcoming = filtered.filter(a => !isPastEventDate(a.event_date, now));
    const past = filtered.filter(a => isPastEventDate(a.event_date, now));

    // sort upcoming ASC by event_date (nulls last)
    upcoming.sort((a, b) => {
      const ta = getEventDateTimestamp(a.event_date);
      const tb = getEventDateTimestamp(b.event_date);
      return ta - tb;
    });
    // sort past DESC by event_date (nulls last)
    past.sort((a, b) => {
      const ta = getEventDateTimestamp(a.event_date, Number.NEGATIVE_INFINITY);
      const tb = getEventDateTimestamp(b.event_date, Number.NEGATIVE_INFINITY);
      return tb - ta;
    });
    return { upcoming, past };
  }, [items, search]);

  return (
    <div className="mx-auto max-w-[1400px]">
      {/* Page title & actions */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6 panel-card panel-card--multi">
        <div>
          <h1 className="text-sm text-slate-600">{headerText}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/panel/eventos/crear"
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200/40 bg-white/80 text-slate-600 transition hover:bg-white hover:text-purple-500"
          >
            +
          </Link>

          <div className="relative">
            <input
              className="w-72 rounded-full bg-white/90 border border-slate-200 pl-10 pr-9 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" /></svg>
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Limpiar búsqueda"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card containers */}
      {loading ? (
        <div className="panel-card panel-card--main p-6 text-slate-500">Cargando…</div>
      ) : (
        <>
          <div className="panel-card panel-card--main p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Próximos eventos</h2>
            {upcoming.length > 0 ? (
              <EventsGrid events={upcoming} />
            ) : (
              <p className="text-slate-500">No hay eventos próximos.</p>
            )}
          </div>

          <div className="panel-card panel-card--main p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Eventos pasados</h2>
            {past.length > 0 ? (
              <EventsGrid events={past} />
            ) : (
              <p className="text-slate-500">No hay eventos pasados.</p>
            )}
          </div>

          <div className="panel-card panel-card--main p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Eventos internos</h2>
            {internalItems.length > 0 ? (
              <EventsGrid events={internalItems} />
            ) : (
              <p className="text-slate-500">No hay eventos internos.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EventsList;
